"use client";

import React, { useState } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { Check, ChevronDown } from "@/lib/icons";
// Removed React Spring - using pure CSS for enterprise-grade smoothness

const certifications = [
  { id: "gas-safe", name: "Gas Safe Registered" },
  { id: "safecontractor", name: "SafeContractor (Alcumus) Approved" },
  { id: "corgi", name: "CORGI Certified (Legacy)" },
];

const manufacturers = [
  { id: "girbau", name: "Girbau", logo: "https://images.prismic.io/forbes-pro/ZvEqPLVsGrYSvqgz_Girbau-Logo-White.png?auto=format,compress" },
  { id: "miele", name: "Miele", logo: "https://images.prismic.io/forbes-pro/ZvEqO7VsGrYSvqgy_Miele-Logo-White.png?auto=format,compress" },
  { id: "speed-queen", name: "Speed Queen", logo: "https://images.prismic.io/forbes-pro/ZvFXzbVsGrYSvrdY_Speedqueen-Logo-White.png?auto=format,compress" },
  { id: "ipso", name: "IPSO", logo: "https://images.prismic.io/forbes-pro/ZvFZNLVsGrYSvrer_IPSO-Logo-White.png?auto=format,compress" },
  { id: "primus", name: "Primus", logo: "https://images.prismic.io/forbes-pro/ZvEqPbVsGrYSvqg0_Primus-Logo-White.png?auto=format,compress" },
  { id: "heubsch", name: "Heubsch", logo: "https://images.prismic.io/forbes-pro/ZvExt7VsGrYSvqkJ_Huebsch-Logo-White.png?auto=format,compress" },
];

const trustIndicators = [
  { title: "Fast", subtitle: "Emergency Response" },
  { title: "Nationwide", subtitle: "Coverage" },
  { title: "Decades", subtitle: "Of Experience" }
];

export function HeroSection() {
  // Better initial state detection to prevent flash
  const [isMobile, setIsMobile] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      // On desktop, always expand; on mobile, keep collapsed
      setIsExpanded(!mobile);
      setIsInitialized(true);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Use CSS animations instead of React Spring for smoother performance
  // const titleSpring = useFadeUp(50);
  // const subtitleSpring = useFadeUp(100);
  // const taglineSpring = useFadeUp(150);
  // const descriptionSpring = useFadeUp(200);
  // const buttonsSpring = useFadeUp(250);
  // const certSpring = useFadeUp(400);

  // Smooth CSS-based expand/collapse instead of jerky React Spring
  const getExpandClasses = () => {
    const baseClasses = "hero-description relative transition-all duration-500 ease-in-out";
    const maskClass = isExpanded && isMobile && isInitialized ? 'mask-none' : '';
    const heightClass = isMobile && isInitialized 
      ? (isExpanded ? 'max-h-none' : 'max-h-[4.5em] overflow-hidden')
      : '';
    return `${baseClasses} ${maskClass} ${heightClass}`.trim();
  };

  return (
    <section 
      id="home" 
      aria-labelledby="hero-heading"
      role="banner"
      className="relative flex flex-col items-center justify-center pt-32 sm:pt-28 md:pt-[12rem] pb-0 overflow-x-hidden bg-blue-900/30"
    >
      {/* Background image - extends to cover hero */}
      <div className="absolute w-full h-full aspect-[16/9] inset-0 -z-20">
        <Image 
          src="/engineer-dave-perkins.png"
          alt="Commercial laundry equipment"
          fill
          className="opacity-60"
          priority
          sizes="100vw"
          style={{
            objectFit: 'cover',
          }}
        />
      </div>
      
      {/* Gradient overlay that transitions to blue at bottom */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-black/30 via-black/50 via-50% to-blue-900/95" />
      
      <div className="max-w-7xl mx-auto w-full">
        <div className="text-left sm:text-center px-5 sm:px-6 md:px-8">
          <h1 
            id="hero-heading"
            className="hero-text px-2 font-bold text-white mb-3 sm:mb-4 leading-[1.1] tracking-tight antialiased animate-fade-up"
            style={{ '--animation-delay': '100ms' } as React.CSSProperties}
          >
            <span className="drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">Commercial Laundry Solutions</span>
          </h1>
          <p 
            className="px-2 text-lg sm:text-2xl md:text-3xl text-white/95 mb-3 sm:mb-4 font-light tracking-wide antialiased animate-fade-up"
            style={{ '--animation-delay': '200ms' } as React.CSSProperties}
            role="doc-subtitle"
          >
            <span className="drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]">Sales. Rentals. Installations. Repairs. Maintenance.</span>
          </p>
          <p 
            className="px-2 text-base sm:text-xl md:text-2xl text-white mb-5 sm:mb-6 max-w-2xl mx-auto font-medium tracking-wide antialiased animate-fade-up"
            style={{ '--animation-delay': '300ms' } as React.CSSProperties}
            role="doc-subtitle"
          >
            <span className="drop-shadow-[0_2px_10px_rgba(0,0,0,0.4)]">Small enough to care. Big enough to deliver.</span>
          </p>
          
          <div className="space-y-8 sm:space-y-12 md:space-y-16">
            <div 
              className="px-2 max-w-3xl mx-auto text-left sm:text-center animate-fade-up"
              style={{ '--animation-delay': '400ms' } as React.CSSProperties}
            >
              <div 
                className={getExpandClasses()}
              >
                <p className="text-sm sm:text-lg md:text-xl text-white/85 leading-relaxed tracking-normal antialiased">
                  <span className="drop-shadow-[0_1px_8px_rgba(0,0,0,0.3)]">
                    At DPCLR, we provide end-to-end laundry equipment solutions to care homes, nursing homes, hospitals, hotels, farms, equine facilities and shared accommodation providers across the UK. From consultancy and CAD design to commissioning, installation, and emergency breakdown response, we bring decades of engineering experience and a uniquely personal approach.                  </span>
                </p>
              </div>
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-2 bg-white/10 backdrop-blur-sm text-white text-sm flex items-center gap-1.5 mx-auto md:hidden hover:bg-white/20 transition-all px-4 py-1.5 rounded-full"
                aria-expanded={isExpanded}
                aria-controls="hero-description"
              >
                {isExpanded ? 'Read less' : 'Read more'}
                <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>
            </div>
            
            <div 
              className="flex flex-col sm:flex-row gap-4 justify-center px-2 animate-fade-up"
              style={{ '--animation-delay': '600ms' } as React.CSSProperties}
            >
              <Button 
                size="lg" 
                asChild 
                className="h-auto rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base sm:text-lg font-semibold shadow-2xl hover:shadow-3xl hover:scale-105 transition-all duration-300 px-6 sm:px-8 py-[12px] sm:py-[18px] tracking-wide"
              >
                <Link href="#contact">Get a Quote</Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                asChild 
                className="h-auto rounded-full border-2 border-white/90 bg-white text-gray-900 text-base sm:text-lg font-semibold hover:bg-white/90 hover:scale-105 transition-all duration-300 px-6 sm:px-8 py-[12px] sm:py-[18px] tracking-wide"
              >
                <Link href="#services">View Services</Link>
              </Button>
            </div>
            
            {/* Trust indicators - simplified */}
            <div 
              className="flex flex-wrap justify-center gap-6 sm:gap-10 md:gap-14 max-w-4xl mx-auto animate-fade-up"
              style={{ '--animation-delay': '800ms' } as React.CSSProperties}
              role="list"
              aria-label="Company highlights"
            >
              {trustIndicators.map((indicator, index) => (
                <div 
                  key={indicator.title}
                  className="text-center"
                  role="listitem"
                >
                  <div className="text-2xl sm:text-4xl font-bold text-sky-500 mb-1.5 sm:mb-2 tracking-tight antialiased">
                    <span className="drop-shadow-[0_2px_10px_rgba(59,130,246,0.5)]" aria-hidden="true">
                      {indicator.title}
                    </span>
                    <span className="sr-only">{`${indicator.title} ${indicator.subtitle}`}</span>
                  </div>
                  <p className="text-sm sm:text-base text-white/90 font-normal tracking-wide antialiased">
                    {indicator.subtitle}
                  </p>
                </div>
              ))}
            </div>
            
            {/* Certifications - streamlined */}
            <div 
              className="mt-12 pt-10 border-t border-blue-300/20 animate-fade-up"
              style={{ '--animation-delay': '1000ms' } as React.CSSProperties}
              role="list"
              aria-label="Certifications"
            >
              <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-5 md:gap-6 text-sm sm:text-base text-white/70">
                {certifications.map((cert) => (
                  <div 
                    key={cert.id}
                    role="listitem"
                    className="flex items-center gap-2 font-medium text-white tracking-wide antialiased"
                  >
                    <Check className="w-5 h-5 text-sky-400 font-bold" strokeWidth={7} aria-hidden="true"/>
                    {cert.name}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Manufacturers - full width marquee with no background */}
      <div 
        className="w-screen relative mt-14 pb-14"
        role="complementary"
        aria-label="Partner manufacturers"
      >
        <h2 className="text-xs sm:text-sm text-white/90 text-center mb-4 sm:mb-6 uppercase tracking-[0.2em] font-light antialiased">
          Working with leading manufacturers
        </h2>
        
        {/* Marquee Container */}
        <div 
          className="relative overflow-hidden py-4"
          role="marquee"
          aria-label="Scrolling manufacturer logos"
        >
          <div className="flex animate-marquee">
            {/* First set of logos */}
            {manufacturers.map((brand) => (
              <div
                key={`${brand.id}-1`}
                className="flex-shrink-0 mx-4 sm:mx-6 md:mx-8"
              >
                <div 
                  className="relative w-32 sm:w-36 md:w-40 h-14 sm:h-16 md:h-20 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity duration-300"
                  role="img"
                  aria-label={`${brand.name} - Manufacturing partner`}
                >
                  <Image
                    src={brand.logo}
                    alt=""
                    fill
                    className="object-contain"
                    sizes="(max-width: 640px) 128px, (max-width: 768px) 144px, 160px"
                    aria-hidden="true"
                  />
                </div>
              </div>
            ))}
            
            {/* Duplicate sets for seamless loop */}
            {manufacturers.map((brand) => (
              <div
                key={`${brand.id}-2`}
                className="flex-shrink-0 mx-4 sm:mx-6 md:mx-8"
                aria-hidden="true"
              >
                <div 
                  className="relative w-32 sm:w-36 md:w-40 h-14 sm:h-16 md:h-20 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity duration-300"
                >
                  <Image
                    src={brand.logo}
                    alt=""
                    fill
                    className="object-contain"
                    sizes="(max-width: 640px) 128px, (max-width: 768px) 144px, 160px"
                  />
                </div>
              </div>
            ))}
            
            {/* Third set for smoother infinite scroll */}
            {manufacturers.map((brand) => (
              <div
                key={`${brand.id}-3`}
                className="flex-shrink-0 mx-4 sm:mx-6 md:mx-8"
                aria-hidden="true"
              >
                <div 
                  className="relative w-32 sm:w-36 md:w-40 h-14 sm:h-16 md:h-20 flex items-center justify-center opacity-90 hover:opacity-100 transition-opacity duration-300"
                >
                  <Image
                    src={brand.logo}
                    alt=""
                    fill
                    className="object-contain"
                    sizes="(max-width: 640px) 128px, (max-width: 768px) 144px, 160px"
                  />
                </div>
              </div>
            ))}
          </div>
          
          {/* Gradient overlays that match the hero gradient */}
          <div className="absolute top-0 left-0 w-32 h-full bg-gradient-to-r from-blue-900 to-transparent z-10 pointer-events-none" />
          <div className="absolute top-0 right-0 w-32 h-full bg-gradient-to-l from-blue-900 to-transparent z-10 pointer-events-none" />
        </div>
      </div>
    </section>
  );
}